// Sim headers, as the five contracts write them (docs/contracts/{hemo,dynamics,cells,pharma,stim}-sim-v0.md).
// Only what the app reads is typed; unknown fields pass through untouched, as the contracts require.

import type { ArrayDescriptor } from './binary.ts';
import type { HeaderTime, NamedTimeBase } from './time.ts';

export type SimFormat = 'hemo' | 'dynamics' | 'cells' | 'pharma' | 'stim' | 'lifetime' | 'sections' | 'thickness-sections';

export const FORMATS: Record<string, SimFormat> = {
  'ba-hemo-sim-v0': 'hemo',
  'ba-dynamics-sim-v0': 'dynamics',
  'ba-cells-sim-v0': 'cells',
  'ba-pharma-sim-v0': 'pharma',
  'ba-stim-sim-v0': 'stim',
  'lifetime-sim-v0': 'lifetime',
  'lifetime-sections-v0': 'sections',
  'lifetime-thickness-sections-v0': 'thickness-sections',
};

/** The asset-pack provenance labels; sims and their inputs use the same five. */
export type Provenance = 'measured' | 'derived' | 'reference' | 'simulated' | 'illustrative';

/** A timed event: hemo and pharma write `{type, t_s}`; dynamics `{t, label, kind}` (seconds). */
export interface SimEvent {
  type?: string;
  kind?: string;
  t_s?: number;
  time_s?: number;
  t?: number;
  segment?: string;
  label?: string;
  [k: string]: unknown;
}

export interface SimModel {
  name?: string;
  id?: string;
  version?: string;
  citation?: string;
  url?: string;
  equations?: string | string[] | Record<string, string>;
  metadata?: string;
  [k: string]: unknown;
}

/** One input. hemo/cells/pharma say `what`; dynamics says `role` and `name`. */
export interface SimInput {
  what?: string;
  role?: string;
  name?: string;
  provenance?: string;
  source?: string;
  method?: string;
  [k: string]: unknown;
}

/** The fields every contract shares (hemo-sim-v0's header, which the others follow). */
export interface CommonHeader {
  format: string;
  id: string;
  kind: string;
  scenario?: { name?: string; label?: string; events?: SimEvent[]; stimuli?: SimEvent[]; [k: string]: unknown };
  created?: string;
  space?: string;
  pack?: { id: string; version?: string } | null;
  provenance?: string;
  methods?: string;
  model?: SimModel;
  inputs?: SimInput[];
  units?: Record<string, string>;
  time?: HeaderTime;
  validation?: { summary?: string; report?: string | null; [k: string]: unknown };
  files?: Record<string, unknown>;
  pipeline?: { path?: string; commit?: string; sha256?: string };
  [k: string]: unknown;
}

// hemo-sim-v0

export interface HemoSegmentRaw {
  id: string;
  index?: number;
  name?: string;
  structure?: string | null;
  sn: number | string;
  tn: number | string;
  centreline?: { offset: number; count: number };
  length_mm?: number;
  radius_mm?: [number, number] | number;
  samples?: { offset: number; count: number; s: number[] };
  state?: 'open' | 'occluded' | 'absent';
  outlet?: { type?: string; territory?: string; [k: string]: unknown } | null;
  geometry?: { provenance?: string; method?: string };
  [k: string]: unknown;
}

export interface HemoGraphRaw {
  nodes: { id: number | string; xyz?: [number, number, number]; kind?: string }[];
  segments: HemoSegmentRaw[];
}

export interface HemoHeader extends CommonHeader {
  files?: {
    graph?: string;
    centrelines?: string;
    centreline_offsets?: string;
    centreline_radius?: string;
    series?: string[];
    volumes?: string[];
    surfaces?: string[];
    growth?: string;
    network?: string;
    [k: string]: unknown;
  };
}

// dynamics-sim-v0

export interface DynamicsParcel {
  index: number;
  source_id?: string;
  source_name?: string;
  structure?: string | null;
  dk_label?: number | null;
  dk_name?: string | null;
  relation?: string;
}

export interface DynamicsHeader extends CommonHeader {
  time_bases?: Record<string, NamedTimeBase & { units?: string }>;
  arrays?: Record<string, ArrayDescriptor>;
  parcels?: DynamicsParcel[];
  channels?: { eeg?: string[]; seeg?: string[]; [k: string]: string[] | undefined };
  display?: { activity_range?: [number, number]; time_scale?: number };
  /** The model's seizure onset, s on its visual and signal bases (a run fitted to a patient's seizure). */
  onset_s?: number;
  projection?: Record<string, unknown>;
  observation?: Record<string, unknown>;
  /** The run's structural network (network.json), bound from distributed-v2 on: node order is the parcels'. */
  network?: { metadata?: string; sha256?: string; node_count?: number; node_order?: string };
  /** Its forward model's sensor tables (forward.json), bound the same way. */
  sensors?: { metadata?: string; sha256?: string };
}

// cells-sim-v0

export interface CellsSeriesInfo {
  quantity: string;
  dtype: string;
  decode?: { offset: number; scale: number };
  units?: string;
  range?: [number, number];
  frames_per_chunk: number;
  chunks: string[];
}

export interface CellsPlacement {
  /** The brain space the affine maps the run's `space` into. */
  space: string;
  /** 4×4 RAS mm, row-major. */
  affine: number[][];
  provenance?: string;
  method?: string;
  [k: string]: unknown;
}

export interface CellsHeader extends CommonHeader {
  counts?: { cells: number; vertices?: number; segments?: number; compartments: number; spikes?: number };
  series?: CellsSeriesInfo[];
  spikes?: { site?: string; threshold_mv?: number };
  placement?: CellsPlacement | null;
  files?: { cells?: string; events?: string | null; synapses?: string | null; lfp?: string | null; [k: string]: unknown };
  /** How the run was recorded: a close-up records some of the cells its simulation ran. */
  recording?: { simulated_cells?: number; [k: string]: unknown };
}

/** One `events.json` entry: a detected dendritic event, a bAP or a stimulus. Times in s. */
export interface CellEvent {
  kind: 'nmda-spike' | 'dendritic-na-spike' | 'ca-spike' | 'bap' | 'stimulus' | 'other' | (string & {});
  cell: number;
  compartments?: number[];
  t_on_s: number;
  t_off_s?: number;
  peak_mv?: number;
  peak_compartment?: number;
  peak_t_s?: number;
  extent_um?: number;
  reach_um?: number;
  /** Where, as the simulator names it: `dend[84](0.75)`. */
  site?: string;
  /** A stimulus's type, as in `scenario.stimuli`: `current-clamp`, `synaptic-cluster`. */
  stimulus?: string;
  [k: string]: unknown;
}

export interface CellEntry {
  index: number;
  id: string;
  name?: string;
  type?: string;
  class?: string;
  layer?: string;
  /** The model as the run names it ("Eyal 2018 active model cell0603_11_model_937 (…)"). */
  model?: string;
  /** Where its morphology comes from and how it was reconstructed. */
  morphology?: { provenance?: string; source?: string; method?: string; file?: string };
  /** Its soma centre in the run's `space`, mm. */
  soma_xyz?: [number, number, number];
  vertices?: { offset: number; count: number };
  segments?: { offset: number; count: number };
  compartments: { offset: number; count: number };
  recording?: 'full' | 'soma';
  [k: string]: unknown;
}

// pharma-sim-v0

export interface PharmaMapDescriptor extends ArrayDescriptor {
  quantity?: string;
  domain: 'parcel' | 'vertex-left' | 'vertex-right' | string;
  target_codes: string[];
  bound?: 'low' | 'central' | 'high' | string;
  time_axis?: number | null;
  mask?: (ArrayDescriptor & { static?: boolean }) | string | null;
}

export interface PharmaSeriesDescriptor extends ArrayDescriptor {
  quantity: string;
  channels: (string | { name?: string; compound?: string; target?: string; [k: string]: unknown })[];
  bound?: string;
  mask?: ArrayDescriptor | string | null;
}

export interface PharmaPrediction {
  target: string;
  affinity?: { low_nm?: number; central_nm?: number; high_nm?: number; n?: number; statistic?: string };
  occupancy?: { low?: number; central?: number; high?: number };
  [k: string]: unknown;
}

export interface PharmaHeader extends CommonHeader {
  name?: string;
  drug?: string;
  target_codes?: string[];
  /** The targets with cortex maps, where the header lists PK targets beyond them. */
  spatial_target_codes?: string[];
  primary_target?: string;
  structure_ids?: string[];
  predictions?: PharmaPrediction[];
  exposure_nm?: { low?: number; central?: number; high?: number };
  excluded?: { target: string; reason?: string }[];
  files?: { maps?: string[]; series?: string[]; surfaces?: string[]; particles?: string[]; events?: string[]; [k: string]: unknown };
}

// stim-sim-v0

/** A coil pose: `matrix` (four rows) takes the coil's local mm to the subject's native RAS+ mm. */
export interface StimPose {
  id: string;
  index: number;
  role?: string;
  offset_mm?: [number, number];
  /** A coil's pose, row-major; a tDCS montage snapshot has none. */
  matrix?: number[][];
  didt_A_per_s?: number;
  scalp_distance_mm?: number;
  /** A tDCS montage snapshot: the header's electrodes by id and their currents, A, summing to zero. */
  electrode_ids?: string[];
  currents_A?: number[];
}

/** A tDCS electrode (`kind: "tdcs"`, the contract's additive section): a rectangular pad on the scalp, its channel and
 *  current, and its span in the `electrode_*` arrays, the FEM's own boundary in the head's RAS+ mm. */
export interface StimElectrode {
  id: string;
  centre?: string;
  pos_ydir?: string;
  shape?: string;
  dimensions?: [number, number];
  thickness?: number;
  channel?: number;
  current_A?: number;
  landmark_center_RAS_mm?: [number, number, number];
  mesh_centroid_RAS_mm?: [number, number, number];
  vertex_offset: number;
  vertex_count: number;
  triangle_offset: number;
  triangle_count: number;
}

/** A span of the cortex's vertices and triangles that is one of the pack's structures. */
export interface StimSurface {
  structure: string;
  vertex_offset: number;
  vertex_count: number;
  triangle_offset: number;
  triangle_count: number;
  mesh?: string;
  mesh_sha256?: string;
}

/** `coil.json`: the coil's model, its source and the electrical model the FEM used. */
export interface StimCoil {
  name: string;
  provenance?: string;
  source?: string | Record<string, unknown>;
  electrical_model?: string | Record<string, unknown>;
  [k: string]: unknown;
}

export interface StimHeader extends CommonHeader {
  format: 'ba-stim-sim-v0';
  arrays: Record<string, ArrayDescriptor>;
  poses?: StimPose[];
  surfaces?: StimSurface[];
  /** tDCS: the montage's pads, in the order the snapshot's `electrode_ids` use. */
  electrodes?: StimElectrode[];
  /** A TMS drag basis (the contract's optional `basis`): the training poses' tangent-grid offsets and the rectangle the live
   *  field is validated on, the seed frame, and the handle landmark (CP3) the coil projector poses with where the bundle
   *  carries its own (IXI165's native one; Ernie's bundle leaves it to the module's frozen rule). */
  basis?: {
    algorithm?: string;
    pose_indices?: number[];
    offsets_mm?: [number, number][];
    valid_rectangle_mm?: [[number, number], [number, number]];
    outside?: string;
    seed_matrix?: number[][];
    seed_scalp_mm?: [number, number, number];
    y_reference_mm?: [number, number, number];
    validation?: string;
    validation_sha256?: string;
    [k: string]: unknown;
  };
  /** One fixed colour range for the bundle, never per pose; tDCS adds its current control, A. */
  display?: {
    quantity?: string;
    units?: string;
    range?: [number, number];
    colormap?: string;
    default_pose?: string;
    current_control?: { reference_A?: number; range_A?: [number, number]; step_A?: number };
  };
}

/** `sims/<domain>/index.json` rows (all five contracts; pharma runs add their own fields). */
export interface IndexEntry {
  id: string;
  kind?: string;
  scenario?: string | { name?: string; label?: string; [k: string]: unknown };
  space?: string;
  path: string;
  created?: string;
  name?: string;
  /** Methods text for the run, written for the header whose SHA-256 is `header_sha256`. */
  methods?: string;
  header_sha256?: string;
  [k: string]: unknown;
}

/** What a run's index row says about it (its label, its Methods text), kept only for the header it was written for. */
export interface IndexAnnotation {
  label?: string;
  methods?: string;
}

// lifetime-sim-v0 (docs/contracts/lifetime-sim-v0.md): a geometry sequence the engine plays through its morph layer.

/** A per-frame scalar map over a surface's vertices (`<f4 [frame, vertex]`), by its published name. */
export interface LifetimeScalar extends ArrayDescriptor {
  name: string;
  units?: string;
  meaning?: string;
}

export interface LifetimeSurface {
  /** The pack structure the surface moves (the engine binds by id, space, vertex count and triangles). */
  id: string;
  name?: string;
  kind?: string;
  vertices?: number;
  triangles?: number;
  indices?: ArrayDescriptor;
  positions?: ArrayDescriptor;
  normals?: ArrayDescriptor;
  scalars?: LifetimeScalar[];
  /** Union bounds over every frame, mm. */
  bounds_mm?: [[number, number, number], [number, number, number]];
}

/** The contract's own time block over the shared keys: seconds since the first frame, every sample listed; never periodic. */
export interface LifetimeTime extends HeaderTime {
  units?: string;
  start?: number;
  end?: number;
  dt?: number;
  values?: number[];
  loop?: boolean;
}

/** The header as the reader hands it on: the contract writes `methods` as named paragraphs, which LifetimeSim.open joins into
 *  the shared string (and keeps apart as `paragraphs`). */
export interface LifetimeHeader extends CommonHeader {
  time?: LifetimeTime;
  /** The frames' ages, for labels: gestational weeks for the fetal atlas. */
  age?: { quantity?: string; units?: string; values?: number[]; at_time_zero?: number };
  surfaces?: LifetimeSurface[];
}

// lifetime-sections-v0 (docs/contracts/lifetime-sections-v0.md): closed 2D sections beside a lifetime-sim-v0 sequence, app-read.

/** One block of sections: a model's saved states (one point count, `positions` `<f4 [frame, point, 3]`) or the atlas's own sampled
 *  weeks (ragged: `positions` `<f4 [N, 3]` with `loop_offsets` `<u4 [loops + 1]` and `frame_loop_offsets` `<u4 [frames + 1]`). */
export interface SectionBlockHeader {
  closed?: boolean;
  kind?: string;
  source_kind?: string;
  structure_id?: string;
  time?: LifetimeTime;
  age?: { quantity?: string; units?: string; values?: number[]; at_time_zero?: number };
  points?: number;
  positions?: ArrayDescriptor;
  loop_offsets?: ArrayDescriptor;
  frame_loop_offsets?: ArrayDescriptor;
  /** The sections' plane: sagittal, its RAS x per frame, and the rule that placed it. */
  plane?: { axis?: string; x_mm?: number[]; rule?: string };
  interpolation?: string;
  meaning?: string;
}

export interface SectionsHeader extends CommonHeader {
  time?: LifetimeTime;
  age?: { quantity?: string; units?: string; values?: number[]; at_time_zero?: number };
  /** The model's growth factor per frame and the clock that maps it to age (an assumed display mapping, the header says). */
  growth?: { units?: string; values?: number[]; clock?: string };
  sections?: { model?: SectionBlockHeader; atlas?: SectionBlockHeader; life015_proxy?: SectionBlockHeader };
  /** How the export asks to be drawn (projection, planes), in its own words. */
  display?: Record<string, string>;
  /** The bets' verdicts, verbatim (LIFE-014, LIFE-015, scope). */
  verdicts?: Record<string, string>;
  bounds_mm?: [[number, number, number], [number, number, number]];
  /** The metrics file's name (metrics.json). */
  metrics?: string;
}

// lifetime-thickness-sections-v0 (docs/contracts/lifetime-thickness-sections-v0.md, ba-life's draft at 21168c51): LIFE-020's four
// assigned-thickness tracks, closed 2D sections on an imposed-growth axis (no age, no seconds, no clock), app-read.

/** One track: a fine-mesh case at an assigned layer thickness, its accepted growth states (a prefix of the requested axis) and their
 *  boundary positions, `<f4 [frames, points, 3]` (the writer records `bytes` and `sha256` beside the shape); `positions` null and
 *  `frames` 0 for a case that never started: no data, never a zero-valued surface. */
export interface ThicknessTrackHeader {
  id: string;
  /** "h = 1.5 mm". */
  label?: string;
  kind?: string;
  structure_id?: string;
  thickness_mm?: number;
  mesh?: string;
  axis?: { quantity?: string; units?: string; values?: number[] };
  points?: number;
  frames?: number;
  closed?: boolean;
  endpoint_duplicated?: boolean;
  /** The section's plane: sagittal at one RAS x, the 21-week source section's. */
  plane?: { axis?: string; x_mm?: number };
  case_status?: string;
  last_accepted_growth?: number | null;
  attempted_loads?: number;
  positions?: (ArrayDescriptor & { bytes?: number }) | null;
  interpolation?: string;
  outside_saved_range?: string;
  /** The first rejected load (the gate's row: its growth, frame and flags), where the gate rejected one. */
  failed_attempt?: { growth?: number; frame?: number; numerical_flags?: Record<string, boolean>; [k: string]: unknown } | null;
  source_frames?: { growth: number; path: string; sha256: string }[];
  [k: string]: unknown;
}

export interface ThicknessSectionsHeader extends CommonHeader {
  domain?: string;
  structure_id?: string;
  coordinates?: { axes?: string; units?: string };
  /** The producer's words on the axis: quasistatic imposed growth, no seconds, age or fitted clock. */
  time_base?: string;
  axis?: { quantity?: string; units?: string; requested_values?: number[] };
  tracks?: ThicknessTrackHeader[];
  /** Fixed bounds over every track and frame, mm, for one physical scale; null with no geometry. */
  bounds_mm?: [[number, number, number], [number, number, number]] | null;
  /** How the export asks to be drawn (projection, no thickness interpolation, display-only geometry interpolation, app-read). */
  display?: Record<string, string>;
  /** The registered result this display copies, bound by hashes: the whole-set verdict first (survives, killed, NOT SCORED). */
  source_result?: { verdict?: string; result_sha256?: string; numerical_gate_sha256?: string; protocol_sha256?: string; simulation_producer_commit?: string };
  /** The metrics file's name (metrics.json: the result and the numerical gate, copied; no new score). */
  metrics?: string;
  scope?: unknown;
}
